import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { GlassCard } from '@/components/ui/glass-card'
import { submitContactForm, ContactApiError, RECAPTCHA_SITE_KEY } from '@/lib/contact-api'
import { CaptchaWidget } from '@gjl/site-kit/captcha'

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', message: '' })
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  // The honeypot's value. Held in its own state rather than in `form` so it can
  // never be spread into the visible controls by a future refactor.
  const [hp, setHp] = useState('')
  // Cleared by the widget on expiry, so someone who pauses to write a long
  // message sends nothing rather than a stale token Google will refuse.
  const [captchaToken, setCaptchaToken] = useState('')
  // Distinguishes "we could not verify you" from the generic failure, because
  // the two have different fixes and only one of them is the visitor's to make.
  const [errorMessage, setErrorMessage] = useState('')

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')

    try {
      // Posts to the ABSOLUTE API origin and requires the API's own
      // `{ success: true }` envelope. Both halves matter: the relative
      // `/api/contact` this replaces never left CloudFront, and `response.ok`
      // was true anyway because the SPA fallback answers 200. See
      // `lib/contact-api.ts` for the full account.
      await submitContactForm({ ...form, _hp: hp, captchaToken })
      setStatus('success')
      setForm({ name: '', email: '', message: '' })
      setCaptchaToken('')
    } catch (err) {
      // ONLY a message the API vouched for. "Verification failed. Please
      // complete the checkbox and try again." is actionable; "Failed to fetch"
      // is a browser string that means nothing to an investor and reads as a
      // broken site, so it falls through to the generic line below.
      setErrorMessage(err instanceof ContactApiError && err.userFacing ? err.message : '')
      setStatus('error')
    }
  }

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="border-border border-b">
        <div className="mx-auto max-w-5xl px-5 pt-14 pb-12 md:px-8 md:pt-20 md:pb-16">
          <h1 className="text-fluid-h1 text-navy mb-6 max-w-4xl">Join the investor list</h1>
          <p className="text-fluid-body-lg text-muted-foreground max-w-2xl">
            Members are notified of each new opportunity before it closes. You can also use this form to ask a question.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="px-5 py-16 md:px-8 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-8 md:grid-cols-5 md:gap-12">
            {/* Form Column */}
            <div className="reveal md:col-span-3">
              {status === 'success' ? (
                <GlassCard accent className="py-12 text-center">
                  <h2 className="text-foreground mb-4 text-2xl font-bold">Message Received</h2>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out! We'll be in touch within 1-2 business days.
                  </p>
                  <Button variant="outline" onClick={() => setStatus('idle')}>
                    Send Another Message
                  </Button>
                </GlassCard>
              ) : (
                <GlassCard>
                  <h2 className="text-foreground mb-6 text-2xl font-semibold">Send a message</h2>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                      <label htmlFor="name" className="text-foreground mb-2 block text-sm font-medium">
                        Name
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="border-input bg-white focus:border-navy"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="text-foreground mb-2 block text-sm font-medium">
                        Email
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className="border-input bg-white focus:border-navy"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="text-foreground mb-2 block text-sm font-medium">
                        Message
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={form.message}
                        onChange={handleChange}
                        required
                        placeholder="Tell us about your investment goals or ask a question..."
                        className="border-input bg-white focus:border-navy"
                      />
                    </div>

                    {/*
                      The honeypot. Hidden from people three ways because one is
                      not enough: off-screen (not `display:none`, which some
                      bots skip), out of the tab order, and hidden from the
                      accessibility tree so a screen-reader user is never asked
                      to fill it. `autoComplete="off"` keeps a browser from
                      helpfully filling it for a real person, which would
                      discard their message.
                    */}
                    <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                      <label htmlFor="_hp">Leave this field empty</label>
                      <input
                        id="_hp"
                        name="_hp"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={hp}
                        onChange={(e) => setHp(e.target.value)}
                      />
                    </div>

                    {/*
                      Renders nothing when no site key is configured, which is
                      the correct state for a build made before the key exists.
                      That is safe only because the server decides whether a
                      token is REQUIRED: a form with no widget and a server that
                      wants one returns 403, never a silent success.
                    */}
                    <CaptchaWidget siteKey={RECAPTCHA_SITE_KEY || undefined} onToken={setCaptchaToken} theme="dark" />

                    {status === 'error' && (
                      <div className="text-destructive bg-destructive/10 rounded-lg p-3 text-sm">
                        {errorMessage || 'Something went wrong. Please try again.'}
                      </div>
                    )}

                    <Button type="submit" size="lg" className="w-full" disabled={status === 'loading'}>
                      {status === 'loading' ? 'Sending...' : 'Send message'}
                    </Button>
                  </form>
                </GlassCard>
              )}
            </div>

            {/* Info Column */}
            <div className="reveal space-y-6 md:col-span-2">
              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">Email</h3>
                    <a
                      href="mailto:garrett@fairmeadow.capital"
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      garrett@fairmeadow.capital
                    </a>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">Investment focus</h3>
                    <p className="text-muted-foreground">
                      Cleveland, Ohio
                      <br />
                      Residential multifamily
                    </p>
                  </div>
                </div>
              </GlassCard>

              <GlassCard className="gold-accent-left pl-6">
                <div className="flex items-start gap-4">
                  <div>
                    <h3 className="text-foreground mb-1 font-semibold">What to expect</h3>
                    <ul className="text-muted-foreground space-y-2 text-sm">
                      <li>Response within 1-2 business days</li>
                      <li>No spam - only deal alerts and updates</li>
                      <li>Direct communication from the founder</li>
                    </ul>
                  </div>
                </div>
              </GlassCard>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
